import React from 'react';
import { Save, CheckCircle, AlertCircle, Info } from 'lucide-react';

/**
 * Componente de notificação flutuante que aparece temporariamente na tela
 * 
 * @param {Object} props - Propriedades do componente
 * @param {boolean} props.show - Define se a notificação deve ser exibida
 * @param {React.ReactNode} props.icon - Ícone a ser exibido na notificação
 * @param {string} props.message - Mensagem a ser exibida
 * @param {string} props.position - Posição da notificação (top-right, top-left, bottom-right, bottom-left, top-center, bottom-center)
 * @param {string} props.type - Tipo da notificação (info, success, error, warning, default)
 * @returns {React.ReactElement} Componente de notificação
 */
const FloatingNotification = ({
  show = false,
  icon,
  message = '',
  position = 'top-center',
  type = 'default'
}) => {
  if (!show) return null;

  // Configuração de posição
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  };

  // Configuração de cores baseada no tipo
  const typeClasses = {
    default: 'bg-white text-gray-800 border-gray-200',
    info: 'bg-blue-50 text-blue-700 border-blue-100',
    success: 'bg-green-50 text-green-700 border-green-100',
    error: 'bg-red-50 text-red-700 border-red-100',
    warning: 'bg-yellow-50 text-yellow-700 border-yellow-100',
  };

  return (
    <div 
      className={`
        fixed z-50 py-2 px-4 rounded-md shadow-md border
        flex items-center animate-fadeIn
        ${positionClasses[position] || positionClasses['top-center']}
        ${typeClasses[type] || typeClasses['default']}
      `}
    >
      {icon && <span className="mr-2">{icon}</span>}
      <span>{message}</span>
    </div>
  );
};

/**
 * Componente específico para notificação de salvamento
 * 
 * @param {Object} props - Propriedades do componente
 * @param {boolean} props.show - Define se a notificação deve ser exibida
 * @returns {React.ReactElement} Componente de notificação de salvamento
 */
const SavingNotification = ({ show }) => (
  <FloatingNotification
    show={show}
    icon={<Save className="h-4 w-4 animate-pulse" />}
    message="Salvando..."
    type="info"
  />
);

export { FloatingNotification, SavingNotification }; 